/* eslint-disable react-refresh/only-export-components */
import { Check, Copy, Pencil, RotateCcw, Save, Trash2 } from "lucide-react";
import type { RaRecord } from "ra-core";
import {
  EditBase,
  Form,
  useDelete,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toSlug } from "@/lib/toSlug";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { NumberInput } from "@/components/admin/number-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import ImageEditorField from "../misc/ImageEditorField";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";
import type { Tag } from "../types";
import { TagEditModal } from "../tags/TagEditModal";

const SECTIONS = [
  {
    id: "branding",
    label: "crm.settings.sections.branding",
    fallback: "Branding",
  },
  {
    id: "companies",
    label: "resources.companies.name",
    fallback: "Companies",
  },
  { id: "deals", label: "resources.deals.name", fallback: "Deals" },
  { id: "notes", label: "resources.notes.name", fallback: "Notes" },
  { id: "tasks", label: "resources.tasks.name", fallback: "Tasks" },
  { id: "tags", label: "resources.tags.name", fallback: "Tags" },
  { id: "mcp", label: "crm.profile.mcp.title", fallback: "MCP Server" },
];

/** Ensure every item in a { value, label } array has a value (slug from label). */
const ensureValues = (items: { value?: string; label: string }[] | undefined) =>
  items?.map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Check for duplicate slugs
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["en"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

const transformFormValues = (data: Record<string, any>) => ({
  config: {
    title: data.title,
    lightModeLogo: data.lightModeLogo,
    darkModeLogo: data.darkModeLogo,
    currency: data.currency,
    dealCurrencies: data.dealCurrencies,
    defaultExchangeRates: data.defaultExchangeRates,
    companySectors: ensureValues(data.companySectors),
    dealCategories: ensureValues(data.dealCategories),
    taskTypes: ensureValues(data.taskTypes),
    dealStages: ensureValues(data.dealStages),
    dealPipelineStatuses: data.dealPipelineStatuses,
    noteStatuses: ensureValues(data.noteStatuses),
  } as ConfigurationContextValue,
});

export const SettingsPage = () => {
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const config = useConfigurationContext();

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      currency: config.currency,
      dealCurrencies: config.dealCurrencies,
      defaultExchangeRates: config.defaultExchangeRates,
      companySectors: config.companySectors,
      dealCategories: config.dealCategories,
      taskTypes: config.taskTypes,
      dealStages: config.dealStages,
      dealPipelineStatuses: config.dealPipelineStatuses,
      noteStatuses: config.noteStatuses,
    }),
    [config],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  const {
    watch,
    setValue,
    reset,
    formState: { isSubmitting },
  } = useFormContext();

  const dealStages = watch("dealStages");
  const dealPipelineStatuses: string[] = watch("dealPipelineStatuses") ?? [];
  const stageDisplayName = translate("crm.settings.validation.entities.stages");
  const categoryDisplayName = translate(
    "crm.settings.validation.entities.categories",
  );

  // isError: when deals fail to load, skip in-use validation rather than blocking forever on "Validating…"
  const { data: deals, isError: dealsError } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const validateDealStages = useCallback(
    (stages: { value: string; label: string }[] | undefined) =>
      // Pass an empty array when deals errored so validation doesn't hang on "Validating…"
      validateItemsInUse(
        stages,
        dealsError ? [] : deals,
        "stage",
        stageDisplayName,
        {
          duplicate: (displayName, duplicates) =>
            translate("crm.settings.validation.duplicate", {
              display_name: displayName,
              items: duplicates.join(", "),
            }),
          inUse: (displayName, inUse) =>
            translate("crm.settings.validation.in_use", {
              display_name: displayName,
              items: inUse.join(", "),
            }),
          validating: translate("crm.settings.validation.validating"),
        },
      ),
    [deals, dealsError, stageDisplayName, translate],
  );

  const validateDealCategories = useCallback(
    (categories: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(
        categories,
        dealsError ? [] : deals,
        "category",
        categoryDisplayName,
        {
          duplicate: (displayName, duplicates) =>
            translate("crm.settings.validation.duplicate", {
              display_name: displayName,
              items: duplicates.join(", "),
            }),
          inUse: (displayName, inUse) =>
            translate("crm.settings.validation.in_use", {
              display_name: displayName,
              items: inUse.join(", "),
            }),
          validating: translate("crm.settings.validation.validating"),
        },
      ),
    [categoryDisplayName, deals, dealsError, translate],
  );

  return (
    <div className="flex gap-8 mt-4 pb-20">
      {/* Left navigation */}
      <nav className="hidden md:block w-48 shrink-0">
        <div className="sticky top-4 space-y-1">
          <h1 className="text-2xl font-semibold px-3 mb-2">
            {translate("crm.settings.title")}
          </h1>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                document
                  .getElementById(section.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors"
            >
              {translate(section.label, { smart_count: 2 })}
            </button>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-2xl space-y-6">
        {/* Branding */}
        <Card id="branding">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.branding")}
            </h2>
            <TextInput source="title" label="crm.settings.app_title" />
            <div className="flex gap-8">
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.light_mode_logo")}
                </p>
                <ImageEditorField
                  source="lightModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#f0f0f0" // --nd-surface-raised light — pinned, not var(), so preview is always light regardless of current theme
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.dark_mode_logo")}
                </p>
                <ImageEditorField
                  source="darkModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#1a1a1a" // --nd-surface-raised dark — pinned, not var(), so preview is always dark regardless of current theme
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Companies */}
        <Card id="companies">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.companies.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.companies.sectors")}
            </h3>
            <ArrayInput
              source="companySectors"
              label={false}
              helperText={false}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Deals */}
        <Card id="deals">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.deals.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.currency")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.deals.currency_help")}
            </p>
            <AutocompleteInput
              source="currency"
              label={false}
              choices={currencyChoices}
              inputText={(choice) => choice?.id}
              modal
            />

            <Separator />

            <DealCurrencySettings />

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.stages")}
            </h3>
            <ArrayInput
              source="dealStages"
              label={false}
              helperText={false}
              validate={validateDealStages}
            >
              <SimpleFormIterator disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.pipeline_statuses")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.deals.pipeline_help")}
            </p>
            <div className="flex flex-wrap gap-2">
              {dealStages?.map(
                (stage: { value: string; label: string }, idx: number) => {
                  const isSelected = dealPipelineStatuses.includes(stage.value);
                  return (
                    <Button
                      key={idx}
                      type="button"
                      variant={isSelected ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (isSelected) {
                          setValue(
                            "dealPipelineStatuses",
                            dealPipelineStatuses.filter(
                              (s) => s !== stage.value,
                            ),
                          );
                        } else {
                          setValue("dealPipelineStatuses", [
                            ...dealPipelineStatuses,
                            stage.value,
                          ]);
                        }
                      }}
                    >
                      {stage.label || stage.value}
                    </Button>
                  );
                },
              )}
            </div>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.categories")}
            </h3>
            <ArrayInput
              source="dealCategories"
              label={false}
              helperText={false}
              validate={validateDealCategories}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card id="notes">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.notes.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.notes.statuses")}
            </h3>
            <ArrayInput source="noteStatuses" label={false} helperText={false}>
              <SimpleFormIterator inline disableReordering disableClear>
                <TextInput source="label" label={false} className="flex-1" />
                <ColorInput source="color" />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card id="tasks">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.tasks.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.tasks.types")}
            </h3>
            <ArrayInput source="taskTypes" label={false} helperText={false}>
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Tags */}
        <TagsSection />

        {/* MCP Server */}
        <McpSection />
      </div>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background p-4">
        <div className="max-w-screen-xl mx-auto flex gap-8 px-4">
          <div className="hidden md:block w-48 shrink-0" />
          <div className="flex-1 min-w-0 max-w-2xl flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                reset({
                  ...defaultConfiguration,
                  lightModeLogo: {
                    src: defaultConfiguration.lightModeLogo,
                  },
                  darkModeLogo: { src: defaultConfiguration.darkModeLogo },
                })
              }
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {translate("crm.settings.reset_defaults")}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
              >
                {translate("ra.action.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                <Save className="h-4 w-4 mr-1" />
                {isSubmitting
                  ? translate("crm.settings.saving")
                  : translate("ra.action.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TagsSection = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  // ID of the tag pending delete confirmation; null = none
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  // ID of the tag actively being deleted (request in-flight)
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const {
    data: tags,
    isPending,
    isError,
    refetch,
  } = useGetList<Tag>("tags", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "name", order: "ASC" },
  });

  const [deleteOne] = useDelete<Tag>();

  const handleDeleteConfirm = async (tag: Tag) => {
    setConfirmDeleteId(null);
    setDeletingId(tag.id);
    try {
      await deleteOne(
        "tags",
        { id: tag.id, previousData: tag },
        {
          onSuccess: () => {
            notify("ra.notification.deleted", {
              type: "success",
              messageArgs: { smart_count: 1 },
            });
            refetch();
          },
          onError: () => {
            notify("ra.notification.http_error", { type: "error" });
          },
        },
      );
    } finally {
      setDeletingId(null);
    }
  };

  const isAnyDeleteInFlight = deletingId !== null;

  return (
    <>
      <Card id="tags">
        <CardContent className="space-y-4">
          <h2 className="text-xl font-semibold text-muted-foreground">
            {translate("resources.tags.name", { smart_count: 2, _: "Tags" })}
          </h2>

          {isPending ? (
            <div
              className="space-y-2"
              aria-busy="true"
              aria-label={translate("ra.page.loading", { _: "Loading" })}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-10 rounded-md bg-muted animate-pulse"
                />
              ))}
            </div>
          ) : isError ? (
            <div className="flex items-center justify-between gap-3 text-sm text-destructive py-1">
              <span>
                {translate("ra.notification.http_error", {
                  _: "Failed to load tags.",
                })}
              </span>
              <button
                type="button"
                onClick={() => refetch()}
                className="underline text-muted-foreground hover:text-foreground transition-colors"
              >
                {translate("ra.action.refresh", { _: "Retry" })}
              </button>
            </div>
          ) : !tags?.length ? (
            <p className="text-sm text-muted-foreground">
              {translate("resources.tags.empty", {
                _: "No tags yet. Create one from a contact's tag list.",
              })}
            </p>
          ) : (
            <ul className="space-y-1">
              {tags.map((tag) => {
                const isPendingConfirm = confirmDeleteId === tag.id;
                const isThisDeleting = deletingId === tag.id;

                return (
                  <li
                    key={tag.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 focus-within:bg-muted/50 transition-colors group"
                  >
                    {/* Color swatch — fallback to border color if tag.color is missing/invalid */}
                    <span
                      className="w-4 h-4 rounded-full shrink-0 border border-border/50"
                      style={{ background: tag.color || "var(--nd-border)" }}
                      aria-hidden="true"
                    />

                    {/* Name — truncate to prevent layout break on long names */}
                    <span
                      className="flex-1 min-w-0 text-sm truncate"
                      title={tag.name}
                    >
                      {tag.name}
                    </span>

                    {isPendingConfirm ? (
                      /* Inline confirmation row */
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          {translate("ra.message.are_you_sure", { _: "Sure?" })}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteConfirm(tag)}
                          disabled={isThisDeleting}
                          className="px-2 py-0.5 rounded text-destructive font-medium hover:bg-destructive/10 transition-colors disabled:opacity-50"
                        >
                          {translate("ra.action.delete", { _: "Delete" })}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="px-2 py-0.5 rounded hover:bg-muted transition-colors"
                        >
                          {translate("ra.action.cancel", { _: "Cancel" })}
                        </button>
                      </span>
                    ) : (
                      /* Normal action buttons — visible on hover/focus, always reachable by keyboard */
                      <>
                        <button
                          type="button"
                          onClick={() => setEditingTag(tag)}
                          disabled={isAnyDeleteInFlight}
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-muted disabled:pointer-events-none"
                          aria-label={`${translate("ra.action.edit")} ${tag.name}`}
                        >
                          <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(tag.id)}
                          disabled={isAnyDeleteInFlight}
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-muted disabled:pointer-events-none"
                          aria-label={`${translate("ra.action.delete")} ${tag.name}`}
                        >
                          {isThisDeleting ? (
                            <span className="w-3.5 h-3.5 block rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {editingTag && (
        <TagEditModal
          tag={editingTag}
          open={!!editingTag}
          onClose={() => setEditingTag(null)}
          onSuccess={async () => {
            setEditingTag(null);
            notify("ra.notification.updated", {
              type: "success",
              messageArgs: { smart_count: 1 },
            });
            refetch();
          }}
        />
      )}
    </>
  );
};

const McpSection = () => {
  const translate = useTranslate();
  return (
    <Card id="mcp">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("crm.profile.mcp.title", { _: "MCP Server" })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("crm.profile.mcp.description", {
            _: "Use this URL to connect your AI assistant to your CRM data via the Model Context Protocol (MCP).",
          })}
        </p>
        <CopyPaste
          value={`${import.meta.env.VITE_SUPABASE_URL?.replace(/\/$/, "")}/functions/v1/mcp`}
        />
      </CardContent>
    </Card>
  );
};

const CopyPaste = ({ value }: { value: string }) => {
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    setCopied(true);
    navigator.clipboard.writeText(value);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            onClick={handleCopy}
            variant="ghost"
            className="normal-case justify-between w-full"
          >
            <span className="overflow-hidden text-ellipsis">{value}</span>
            {copied ? (
              <Check className="h-4 w-4 ml-2" />
            ) : (
              <Copy className="h-4 w-4 ml-2" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {copied
              ? translate("crm.common.copied", { _: "Copied!" })
              : translate("crm.common.copy", { _: "Copy" })}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};

/**
 * Deal currencies are fixed by the DB check constraint (ADR-010), so they are
 * listed read-only here. The seed exchange rate for each non-reporting
 * currency is editable; each deal still keeps its own rate.
 */
const DealCurrencySettings = () => {
  const translate = useTranslate();
  const { watch } = useFormContext();
  const reportingCurrency: string = watch("currency");
  const dealCurrencies: string[] = watch("dealCurrencies") ?? [];
  const foreignCurrencies = dealCurrencies.filter(
    (code) => code !== reportingCurrency,
  );

  return (
    <>
      <h3 className="text-lg font-medium text-muted-foreground">
        {translate("crm.settings.deals.deal_currencies")}
      </h3>
      <p className="text-sm text-muted-foreground">
        {translate("crm.settings.deals.deal_currencies_help")}
      </p>
      <div className="flex flex-wrap gap-2">
        {dealCurrencies.map((code) => (
          <span
            key={code}
            className="font-nd-mono text-xs px-2 py-1 rounded-sm border border-nd-border text-nd-text-primary"
          >
            {code}
          </span>
        ))}
      </div>

      {foreignCurrencies.length > 0 && (
        <>
          <h3 className="text-lg font-medium text-muted-foreground pt-2">
            {translate("crm.settings.deals.exchange_rates")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.deals.exchange_rates_help")}
          </p>
          <div className="flex flex-wrap gap-4">
            {foreignCurrencies.map((code) => (
              <NumberInput
                key={code}
                source={`defaultExchangeRates.${code}`}
                label={`${reportingCurrency} / ${code}`}
                step={0.0001}
                min={0}
                helperText={false}
                className="w-40"
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};
